import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import styles from "./AppSwitcher.module.css";

export const MARKETING_ADMIN_URL: string = import.meta.env.VITE_MARKETING_ADMIN_URL ?? "https://api.zerotoclaw.ai/admin";

const GridIcon = (): JSX.Element => (
  <svg className={styles.gridIcon} viewBox="0 0 16 16" aria-hidden="true">
    {[3, 8, 13].flatMap((y) => [3, 8, 13].map((x) => <circle key={`${x}-${y}`} cx={x} cy={y} r="1.35" />))}
  </svg>
);

const PlatformIcon = (): JSX.Element => (
  <svg className={styles.appIcon} viewBox="0 0 16 16" aria-hidden="true">
    <rect x="2.5" y="2.5" width="11" height="4" rx="1.25" />
    <rect x="2.5" y="9.5" width="11" height="4" rx="1.25" />
    <path d="M5 4.5h.01M5 11.5h.01" />
  </svg>
);

const MarketingIcon = (): JSX.Element => (
  <svg className={styles.appIcon} viewBox="0 0 16 16" aria-hidden="true">
    <path d="M2.5 6.5v3a1 1 0 0 0 1 1h1.5l5 3v-11l-5 3H3.5a1 1 0 0 0-1 1Z" />
    <path d="M12.5 6a2.5 2.5 0 0 1 0 4" />
  </svg>
);

const CheckIcon = (): JSX.Element => (
  <svg className={styles.check} viewBox="0 0 16 16" aria-hidden="true">
    <path d="m3.5 8.5 3 3 6-7" />
  </svg>
);

// The marketing admin is a separate site in a separate AWS account with its
// own sign-in, so it is a plain cross-origin link: nothing is shared or
// handed over. Mirrored by the marketing admin's #apps menu.
// Rendered on the admin routes only; customer-facing pages never show it.
export const AppSwitcher = (): JSX.Element => {
  const [open, setOpen] = useState<boolean>(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onMouseDown = (event: MouseEvent): void => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== "Escape") return;
      setOpen(false);
      if (rootRef.current?.contains(document.activeElement)) triggerRef.current?.focus();
    };
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <div ref={rootRef} className={styles.root}>
      <button
        ref={triggerRef}
        type="button"
        className={`${styles.trigger} ${open ? styles.triggerOpen : ""}`}
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
        aria-controls="app-switcher-panel"
        aria-label="Switch app"
        title="Switch app"
      >
        <GridIcon />
      </button>

      <nav id="app-switcher-panel" className={`${styles.panel} ${open ? styles.panelOpen : ""}`} aria-label="Switch app">
        <span className={styles.heading}>Switch to</span>
        <Link
          to="/dashboard/admin"
          className={`${styles.app} ${styles.appCurrent}`}
          aria-current="page"
          onClick={() => setOpen(false)}
        >
          <span className={styles.tile}>
            <PlatformIcon />
          </span>
          <span className={styles.text}>
            <span className={styles.name}>Platform admin</span>
            <span className={styles.desc}>Customers, instances, billing</span>
          </span>
          <CheckIcon />
        </Link>
        <a className={styles.app} href={MARKETING_ADMIN_URL} rel="noreferrer">
          <span className={styles.tile}>
            <MarketingIcon />
          </span>
          <span className={styles.text}>
            <span className={styles.name}>Marketing admin</span>
            <span className={styles.desc}>Leads, bookings, SMS, Meta</span>
          </span>
        </a>
      </nav>
    </div>
  );
};
